import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NAV_LINKS, PULSE_TESTIDS } from "@/lib/pulseConstants";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";
import { cn } from "@/lib/utils";
import PulseWordmark from "@/components/shared/PulseWordmark";

export default function SiteHeader() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Mirror menu open state to a document-level attribute so mobile-only
    // components (e.g. the sticky CTA) can hide themselves when the Sheet
    // menu is open, without needing shared React state or a store.
    useEffect(() => {
        const root = document.documentElement;
        if (open) {
            root.dataset.mobileMenuOpen = "true";
        } else {
            delete root.dataset.mobileMenuOpen;
        }
        return () => {
            delete root.dataset.mobileMenuOpen;
        };
    }, [open]);

    return (
        <header
            data-testid={PULSE_TESTIDS.nav.header}
            className={cn(
                "sticky top-0 z-50 w-full border-b transition-colors duration-300",
                scrolled
                    ? "bg-black/75 backdrop-blur-xl border-white/10"
                    : "bg-black/30 backdrop-blur-md border-transparent"
            )}
        >
            <div className="container-editorial h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    aria-label="REFORM Pulse — back to homepage"
                    className="group"
                    data-testid="nav-logo"
                >
                    <PulseWordmark size="md" showTagline className="hidden sm:flex" />
                    <PulseWordmark size="sm" showTagline={false} className="sm:hidden" />
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testId}
                            className={({ isActive }) =>
                                cn(
                                    "px-4 py-2 rounded-lg text-sm font-medium tracking-wide transition-colors",
                                    isActive
                                        ? "text-white bg-white/5"
                                        : "text-white/70 hover:text-white hover:bg-white/5"
                                )
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <Button
                        asChild
                        className="hidden md:inline-flex bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full px-5 h-10 group"
                        data-testid={PULSE_TESTIDS.nav.primaryCta}
                    >
                        <Link to="/contact" onClick={handlePulseBriefClick} className="inline-flex items-center gap-1.5">
                            Start Your Pulse Brief
                            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </Button>

                    {/* Mobile trigger */}
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                aria-label="Open menu"
                                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/80 hover:text-white hover:bg-white/5"
                                data-testid={PULSE_TESTIDS.nav.mobileMenu}
                            >
                                <Menu size={18} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-full sm:max-w-md bg-[#07070A] border-l border-white/10 text-white p-0">
                            <SheetTitle className="sr-only">Site navigation</SheetTitle>
                            <SheetDescription className="sr-only">Access all pages of REFORM Pulse</SheetDescription>
                            <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
                                <PulseWordmark size="sm" showTagline />
                                <button
                                    aria-label="Close menu"
                                    className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center"
                                    onClick={() => setOpen(false)}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <nav className="px-5 py-6 flex flex-col gap-1">
                                {NAV_LINKS.map((item, idx) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        data-testid={`${item.testId}-mobile`}
                                        className={({ isActive }) =>
                                            cn(
                                                "flex items-center justify-between py-4 px-4 rounded-xl text-lg font-medium border",
                                                isActive
                                                    ? "text-white bg-[#FF2A2A]/10 border-[#FF2A2A]/40"
                                                    : "text-white/80 hover:text-white border-white/10 hover:border-white/25"
                                            )
                                        }
                                    >
                                        <span>{item.label}</span>
                                        <span className="font-mono text-xs text-white/40">{String(idx + 1).padStart(2, "0")}</span>
                                    </NavLink>
                                ))}
                                <Button
                                    asChild
                                    className="mt-6 bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12"
                                >
                                    <Link
                                        to="/contact"
                                        onClick={(e) => { handlePulseBriefClick(e); setOpen(false); }}
                                        data-testid="nav-mobile-primary-cta"
                                    >
                                        Start Your Pulse Brief
                                    </Link>
                                </Button>
                                <div className="mt-8 pt-6 border-t border-white/10 text-xs font-mono text-white/50 uppercase tracking-widest">
                                    Kuala Lumpur → SEA
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
